import { Link, router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { useState } from 'react'
import type { DealOfferingDetail, DealOfferingInvitationSummary, DealOfferingStatus } from '@/types/deal_offering'

interface CommitmentSummary {
  id: number
  investor_name: string
  amount: number
  status: string
  committed_at: string | null
  withdrawn_at: string | null
}

interface ParticipationSummary {
  id: number
  investor_name: string
  principal_amount: number
  ownership_percent: number
}

interface AvailableInvestor {
  id: number
  name: string
  email: string | null
}

interface Props {
  offering: DealOfferingDetail
  invitations: DealOfferingInvitationSummary[]
  commitments: CommitmentSummary[]
  participations: ParticipationSummary[]
  available_investors: AvailableInvestor[]
}

const STATUS_BADGES: Record<DealOfferingStatus, { label: string; bg: string; text: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100 border-gray-200', text: 'text-gray-600' },
  open: { label: 'Open', bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
  locked: { label: 'Locked', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
  funded: { label: 'Funded', bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-50 border-red-200', text: 'text-red-700' },
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function DealOfferingsShow({ offering, invitations, commitments, participations, available_investors }: Props) {
  const { app_user } = usePage<{ app_user: { is_member: boolean } | null }>().props
  const isMember = app_user?.is_member === true
  const badge = STATUS_BADGES[offering.status] || STATUS_BADGES.draft
  const [showLockModal, setShowLockModal] = useState(false)
  const [locking, setLocking] = useState(false)
  const [selectedNewInvestorIds, setSelectedNewInvestorIds] = useState<number[]>([])
  const [inviting, setInviting] = useState(false)

  const activeCommitments = commitments.filter((c) => c.status === 'confirmed')

  function toggleNewInvestor(id: number) {
    setSelectedNewInvestorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  function handleAddInvestors() {
    if (selectedNewInvestorIds.length === 0) return
    setInviting(true)
    router.post(
      `/deal_offerings/${offering.id}/add_investors`,
      { investor_ids: selectedNewInvestorIds },
      {
        onFinish: () => {
          setInviting(false)
          setSelectedNewInvestorIds([])
        },
      },
    )
  }

  function handleCancel() {
    if (confirm('Are you sure you want to cancel this offering?')) {
      router.post(`/deal_offerings/${offering.id}/cancel`)
    }
  }

  function handleLock() {
    setLocking(true)
    router.post(`/deal_offerings/${offering.id}/lock`, {}, {
      onFinish: () => {
        setLocking(false)
        setShowLockModal(false)
      },
    })
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Link href="/deal_offerings" className="text-sm text-gray-500 hover:text-gray-700">
            &larr; Back to Offerings
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900">{offering.borrower_name}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-sm text-gray-500">{offering.loan_number}</p>
          </div>
          <div className="flex items-center gap-2">
            {offering.status === 'draft' && (
              <Link
                href={`/deal_offerings/${offering.id}/edit`}
                className="px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                Edit
              </Link>
            )}
            {offering.status === 'open' && activeCommitments.length > 0 && !isMember && (
              <button
                onClick={() => setShowLockModal(true)}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Lock Offering
              </button>
            )}
            {offering.status === 'open' && activeCommitments.length > 0 && isMember && (
              <span className="text-xs text-gray-500 italic px-2">
                Only the account owner can lock offerings.
              </span>
            )}
            {(offering.status === 'draft' || offering.status === 'open') && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Total Amount</p>
            <p className="text-lg font-bold text-gray-900">${formatCurrency(offering.total_amount)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Committed</p>
            <p className="text-lg font-bold text-emerald-600">${formatCurrency(offering.committed_amount)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">Remaining</p>
            <p className="text-lg font-bold text-gray-900">${formatCurrency(offering.remaining_amount)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 mb-1">% Funded</p>
            <p className="text-lg font-bold text-gray-900">{offering.percent_funded}%</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Funding Progress</h3>
            <span className="text-sm font-bold text-emerald-600">{offering.percent_funded}%</span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(offering.percent_funded, 100)}%` }}
            />
          </div>
        </div>

        {/* Loan Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Loan Details</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-400 text-xs">Principal</p>
              <p className="font-medium text-gray-900">${formatCurrency(offering.loan_principal)}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs">Rate</p>
              <p className="font-medium text-gray-900">{offering.loan_rate}%</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs">Term</p>
              <p className="font-medium text-gray-900">{offering.loan_term} months</p>
            </div>
            {offering.min_commitment && (
              <div>
                <p className="text-gray-400 text-xs">Min Commitment</p>
                <p className="font-medium text-gray-900">${formatCurrency(offering.min_commitment)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Deal Memo */}
        {offering.deal_memo && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Deal Memo</h3>
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{offering.deal_memo}</div>
          </div>
        )}

        {/* Participations (shown after lock) */}
        {offering.status === 'locked' && participations.length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 overflow-hidden mb-6">
            <div className="p-5 border-b border-amber-100 bg-amber-50">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-amber-800">
                  Participations Created ({participations.length})
                </h3>
                <Link
                  href={`/loans/${offering.loan_id}`}
                  className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                >
                  View Loan &rarr;
                </Link>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {participations.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3">
                  <p className="text-sm font-medium text-gray-900">{p.investor_name}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-700">${formatCurrency(p.principal_amount)}</span>
                    <span className="text-gray-500">{p.ownership_percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commitments (shown when open or locked for audit trail) */}
        {commitments.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">
                Soft Commitments ({commitments.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {commitments.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{c.investor_name}</p>
                    {c.committed_at && (
                      <p className="text-xs text-gray-400">
                        {new Date(c.committed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">${formatCurrency(c.amount)}</span>
                    {c.status === 'withdrawn' ? (
                      <span className="text-xs text-red-500 font-medium">Withdrawn</span>
                    ) : (
                      <span className="text-xs text-emerald-600 font-medium">Confirmed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invite additional investors — open offerings only */}
        {offering.status === 'open' && available_investors.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Invite more investors</h3>
              <p className="text-xs text-gray-500 mt-1">
                Select additional investors to invite. Each will receive the same deal invitation email.
              </p>
            </div>
            <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {available_investors.map((inv) => {
                const checked = selectedNewInvestorIds.includes(inv.id)
                return (
                  <label
                    key={inv.id}
                    className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleNewInvestor(inv.id)}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{inv.name}</p>
                        {inv.email && <p className="text-xs text-gray-400">{inv.email}</p>}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
            <div className="p-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
              <p className="text-xs text-gray-500">
                {selectedNewInvestorIds.length} selected
              </p>
              <button
                onClick={handleAddInvestors}
                disabled={selectedNewInvestorIds.length === 0 || inviting}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                {inviting ? 'Sending…' : 'Send invitations'}
              </button>
            </div>
          </div>
        )}

        {/* Invitations */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              Invited Investors ({invitations.length})
            </h3>
          </div>
          {invitations.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No investors invited yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {invitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{inv.investor_name}</p>
                    {inv.investor_email && (
                      <p className="text-xs text-gray-400">{inv.investor_email}</p>
                    )}
                  </div>
                  <div className="text-right">
                    {inv.viewed_at ? (
                      <span className="text-xs text-emerald-600 font-medium">Viewed</span>
                    ) : inv.invited_at ? (
                      <span className="text-xs text-gray-400">
                        Sent {new Date(inv.invited_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Pending</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Timestamps */}
        <div className="mt-4 flex items-center gap-4 text-xs text-gray-400">
          <span>Created {new Date(offering.created_at).toLocaleDateString()}</span>
          {offering.published_at && <span>Published {new Date(offering.published_at).toLocaleDateString()}</span>}
          {offering.locked_at && <span>Locked {new Date(offering.locked_at).toLocaleDateString()}</span>}
        </div>
      </div>

      {/* Lock Confirmation Modal */}
      {showLockModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Lock This Offering?</h2>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              This will lock the offering at <strong>${formatCurrency(offering.committed_amount)}</strong> committed
              of <strong>${formatCurrency(offering.total_amount)}</strong> total and create{' '}
              <strong>{activeCommitments.length}</strong> permanent participation
              {activeCommitments.length === 1 ? '' : 's'}.
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
              This action cannot be undone. No new commitments or withdrawals will be accepted after locking.
            </p>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowLockModal(false)}
                disabled={locking}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLock}
                disabled={locking}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {locking ? 'Locking...' : 'Lock Offering'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
